/**
 * ADA Fix: Status Messages Programmatic Determination
 * 
 * Issue: Status messages (errors/success) are not properly announced to screen readers
 * because they lack proper ARIA attributes or the attributes are not maintained when
 * content is updated dynamically.
 * 
 * Solution: Ensures all status message containers have aria-live="polite" and 
 * aria-atomic="true", and these attributes persist through dynamic content updates.
 */

(function() {
  'use strict';

  // Selectors for various status message containers
  const STATUS_SELECTORS = [
    '.error_message',
    '.success_message',
    '.klaviyo_messages .error_message',
    '.klaviyo_messages .success_message',
    '.form-message',
    '[class*="message"]',
    '[class*="alert"]'
  ];

  /**
   * Ensures a status message element has proper ARIA attributes
   */
  function ensureAriaAttributes(element) {
    if (!element || element.nodeType !== 1) return;

    // Add aria-live if not present or incorrect
    if (!element.hasAttribute('aria-live') || element.getAttribute('aria-live') !== 'polite') {
      element.setAttribute('aria-live', 'polite');
    }

    // Add aria-atomic if not present
    if (!element.hasAttribute('aria-atomic')) {
      element.setAttribute('aria-atomic', 'true');
    }

    // Add role="status" if it's a status message and doesn't have role="alert"
    if (!element.hasAttribute('role')) {
      // If it's an error message, use alert, otherwise use status
      if (element.classList.contains('error_message') || 
          element.classList.contains('error') ||
          element.textContent.toLowerCase().includes('error')) {
        element.setAttribute('role', 'alert');
      } else {
        element.setAttribute('role', 'status');
      }
    }
  }

  /**
   * Process all status message elements on the page
   */
  function processStatusMessages() {
    // Process error_message and success_message elements
    document.querySelectorAll('.error_message, .success_message').forEach(function(msg) {
      ensureAriaAttributes(msg);
    });

    // Process other potential status message elements
    document.querySelectorAll('.form-message, [role="alert"], [role="status"]').forEach(function(msg) {
      ensureAriaAttributes(msg);
    });
  }

  /**
   * Force screen reader announcement by temporarily changing content
   * This is a workaround for when aria-live regions don't update properly
   */
  function forceAnnouncement(element) {
    if (!element || !element.textContent.trim()) return;
    
    const display = window.getComputedStyle(element).display;
    if (display === 'none') return;

    // Store original content
    const originalContent = element.innerHTML;
    
    // Temporarily clear and restore content to trigger aria-live
    element.innerHTML = '';
    
    requestAnimationFrame(function() {
      element.innerHTML = originalContent;
    });
  }

  /**
   * Watch for visibility changes and ensure announcement
   */
  function handleVisibilityChange(element) {
    const display = window.getComputedStyle(element).display;
    
    if (display !== 'none' && element.textContent.trim()) {
      ensureAriaAttributes(element);
      
      // Small delay to ensure screen readers pick up the change
      setTimeout(function() {
        // Re-apply aria-live to ensure announcement
        element.setAttribute('aria-live', 'off');
        
        requestAnimationFrame(function() {
          element.setAttribute('aria-live', 'polite');
        });
      }, 50);
    }
  }

  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', processStatusMessages);
  } else {
    processStatusMessages();
  }

  // Create MutationObserver to watch for dynamic changes
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      // Handle attribute changes (especially style changes for show/hide)
      if (mutation.type === 'attributes') {
        if (mutation.attributeName === 'style' || mutation.attributeName === 'class') {
          const element = mutation.target;
          
          if (element.classList.contains('error_message') || 
              element.classList.contains('success_message') ||
              element.classList.contains('form-message')) {
            
            ensureAriaAttributes(element);
            handleVisibilityChange(element);
          }
        }
      }
      
      // Handle content changes
      if (mutation.type === 'characterData' || mutation.type === 'childList') {
        let targetElement = mutation.target;
        
        // If target is text node, get parent element
        if (targetElement.nodeType === 3) {
          targetElement = targetElement.parentElement;
        }
        
        if (targetElement && 
            (targetElement.classList.contains('error_message') || 
             targetElement.classList.contains('success_message') ||
             targetElement.classList.contains('form-message'))) {
          
          ensureAriaAttributes(targetElement);
          
          const display = window.getComputedStyle(targetElement).display;
          if (display !== 'none' && targetElement.textContent.trim()) {
            handleVisibilityChange(targetElement);
          }
        }
      }
      
      // Handle newly added nodes
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) {
            // Check if the node itself is a status message
            if (node.classList && 
                (node.classList.contains('error_message') || 
                 node.classList.contains('success_message') ||
                 node.classList.contains('form-message'))) {
              ensureAriaAttributes(node);
            }
            
            // Check for status messages within the node
            if (node.querySelectorAll) {
              node.querySelectorAll('.error_message, .success_message, .form-message').forEach(function(msg) {
                ensureAriaAttributes(msg);
              });
            }
          }
        });
      }
    });
  });

  // Observe the entire document for changes
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class'],
    characterData: true
  });

  // Re-check every few seconds as a safety net for missed updates
  setInterval(processStatusMessages, 3000);

})();
